/**
 * Utility to transform table data to chart-compatible format (UniversalChartData)
 * Uses llm_payload.columns to identify dimension and measure columns
 */

export interface LlmColumn {
  name: string;
  displayName: string;
  dataType: 'categorical' | 'numeric' | 'date' | 'text';
  role: 'dimension' | 'measure';
  format?: string | null;
}

export interface TableData {
  headers: string[];
  rows: (string | number)[][];
}

export interface ChartSeries {
  name: string;
  data: number[];
  stack?: string;
}

export interface UniversalChartData {
  type: 'bar' | 'column' | 'line' | 'pie' | 'stackedColumn';
  title?: string;
  subtitle?: string;
  categories: string[];
  series: ChartSeries[];
  xAxisTitle?: string;
  yAxisTitle?: string;
  stacking?: 'normal';
  showLegend?: boolean;
  colors?: string[];
}

export interface ChartConfig {
  dimensionColumn: string;
  secondaryDimensionColumn?: string; // For bar/column charts: creates grouped/stacked series
  measureColumns: string[];
}

/**
 * Parse a numeric value from a formatted string (e.g., "6,257,675.54" -> 6257675.54)
 */
const parseNumericValue = (value: string | number): number => {
  if (typeof value === 'number') return value;
  if (!value || value === '') return 0;

  // Remove currency symbols, commas, spaces, and percentage signs
  const cleaned = value.toString()
    .replace(/[$€£¥₹,\s]/g, '')
    .replace(/%$/, '');

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Find the column index in headers by column name (case-insensitive)
 */
const findColumnIndex = (headers: string[], columnName: string): number => {
  const lowerName = columnName.toLowerCase();
  return headers.findIndex(h =>
    h.toLowerCase() === lowerName ||
    h.toLowerCase().replace(/[_\s]/g, '') === lowerName.replace(/[_\s]/g, '')
  );
};

/**
 * Get the first dimension column from llm_payload.columns
 */
export const getDefaultDimensionColumn = (columns: LlmColumn[]): string | null => {
  const dimensionCol = columns.find(col => col.role === 'dimension');
  return dimensionCol?.name || null;
};

/**
 * Get the first measure column from llm_payload.columns
 */
export const getDefaultMeasureColumn = (columns: LlmColumn[]): string | null => {
  const measureCol = columns.find(col => col.role === 'measure');
  return measureCol?.name || null;
};

/**
 * Get all dimension columns from llm_payload.columns
 */
export const getDimensionColumns = (columns: LlmColumn[]): LlmColumn[] => {
  return columns.filter(col => col.role === 'dimension');
};

/**
 * Get all measure columns from llm_payload.columns
 */
export const getMeasureColumns = (columns: LlmColumn[]): LlmColumn[] => {
  return columns.filter(col => col.role === 'measure');
};

/**
 * Transform table data to chart data format
 * 
 * @param tableData - Table data with headers and rows
 * @param columns - LLM payload columns with role information
 * @param chartType - Type of chart to create
 * @param config - Optional chart configuration (dimension and measure columns)
 * @returns UniversalChartData compatible with chart components
 */
export const transformTableToChartData = (
  tableData: TableData,
  columns: LlmColumn[],
  chartType: 'barchart' | 'columnchart' | 'linechart' | 'piechart',
  config?: ChartConfig
): UniversalChartData | null => {
  if (!tableData || !tableData.headers || !tableData.rows || tableData.rows.length === 0) {
    return null;
  }

  // Get dimension and measure columns
  const dimensionColumnName = config?.dimensionColumn || getDefaultDimensionColumn(columns);
  const secondaryDimensionColumnName = config?.secondaryDimensionColumn;
  const measureColumnNames = config?.measureColumns ||
    [getDefaultMeasureColumn(columns)].filter(Boolean) as string[];

  if (!dimensionColumnName || measureColumnNames.length === 0) {
    return null;
  }

  // Find primary dimension column index
  let dimensionIndex = findColumnIndex(tableData.headers, dimensionColumnName);
  if (dimensionIndex === -1) {
    const dimensionCol = columns.find(c => c.name === dimensionColumnName);
    if (dimensionCol) {
      dimensionIndex = findColumnIndex(tableData.headers, dimensionCol.displayName);
    }
  }

  if (dimensionIndex === -1) {
    return null;
  }

  // Check if we have a secondary dimension (only for bar/column charts)
  const supportedGroupedTypes = ['barchart', 'columnchart'];
  const hasSecondaryDimension = secondaryDimensionColumnName &&
    secondaryDimensionColumnName !== dimensionColumnName &&
    supportedGroupedTypes.includes(chartType);

  if (hasSecondaryDimension) {
    // GROUPED CHART: Secondary dimension creates series
    // Find secondary dimension column index
    let secondaryDimensionIndex = findColumnIndex(tableData.headers, secondaryDimensionColumnName);
    if (secondaryDimensionIndex === -1) {
      const secondaryCol = columns.find(c => c.name === secondaryDimensionColumnName);
      if (secondaryCol) {
        secondaryDimensionIndex = findColumnIndex(tableData.headers, secondaryCol.displayName);
      }
    }

    if (secondaryDimensionIndex === -1) {
      return null;
    }

    // For grouped charts, use only the first measure
    const measureColumnName = measureColumnNames[0];
    let measureIndex = findColumnIndex(tableData.headers, measureColumnName);
    if (measureIndex === -1) {
      const measureCol = columns.find(c => c.name === measureColumnName);
      if (measureCol) {
        measureIndex = findColumnIndex(tableData.headers, measureCol.displayName);
      }
    }

    if (measureIndex === -1) {
      return null;
    }

    // Get unique primary dimension values (categories)
    const uniqueCategories = Array.from(new Set(
      tableData.rows
        .map(row => row[dimensionIndex]?.toString() || '')
        .filter(v => v !== '')
    ));

    // Get unique secondary dimension values (series names)
    const uniqueSeriesNames = Array.from(new Set(
      tableData.rows
        .map(row => row[secondaryDimensionIndex]?.toString() || '')
        .filter(v => v !== '')
    ));

    // Create a pivot map: category -> seriesName -> value
    const pivotMap = new Map<string, Map<string, number>>();
    uniqueCategories.forEach(cat => {
      const seriesMap = new Map<string, number>();
      uniqueSeriesNames.forEach(s => seriesMap.set(s, 0));
      pivotMap.set(cat, seriesMap);
    });

    // Populate the pivot map with actual values
    tableData.rows.forEach(row => {
      const category = row[dimensionIndex]?.toString() || '';
      const seriesName = row[secondaryDimensionIndex]?.toString() || '';
      const value = parseNumericValue(row[measureIndex]);

      if (category && seriesName && pivotMap.has(category)) {
        const seriesMap = pivotMap.get(category)!;
        // Aggregate values for same category + series combination
        seriesMap.set(seriesName, (seriesMap.get(seriesName) || 0) + value);
      }
    });

    // Create series from pivot map
    const series: ChartSeries[] = uniqueSeriesNames.map(seriesName => ({
      name: seriesName,
      data: uniqueCategories.map(cat => pivotMap.get(cat)?.get(seriesName) || 0)
    }));

    // Get axis titles
    const dimensionCol = columns.find(c => c.name === dimensionColumnName);
    const xAxisTitle = dimensionCol?.displayName || tableData.headers[dimensionIndex];

    // Use stackedColumn type for secondary dimension grouping
    return {
      type: 'stackedColumn' as const,
      categories: uniqueCategories,
      series,
      xAxisTitle,
      showLegend: true // Always show legend for stacked charts
    };
  }

  // STANDARD CHART: Multiple measures as series
  const measureIndices = measureColumnNames.map(name => {
    let index = findColumnIndex(tableData.headers, name);
    if (index === -1) {
      const measureCol = columns.find(c => c.name === name);
      if (measureCol) {
        index = findColumnIndex(tableData.headers, measureCol.displayName);
      }
    }
    return index;
  });

  if (measureIndices.some(i => i === -1)) {
    return null;
  }

  // Extract categories (dimension values)
  const categories = tableData.rows.map(row => {
    const value = row[dimensionIndex];
    return value?.toString() || '';
  });

  // Extract series (measure values)
  const series: ChartSeries[] = measureColumnNames.map((columnName, idx) => {
    const measureIndex = measureIndices[idx];
    const column = columns.find(c => c.name === columnName);
    const seriesName = column?.displayName || tableData.headers[measureIndex] || columnName;

    const data = tableData.rows.map(row => {
      const value = row[measureIndex];
      return parseNumericValue(value);
    });

    return {
      name: seriesName,
      data
    };
  });

  // Map chart type to internal type
  const typeMap: Record<string, 'bar' | 'column' | 'line' | 'pie'> = {
    'barchart': 'bar',
    'columnchart': 'column',
    'linechart': 'line',
    'piechart': 'pie'
  };

  // Get dimension and measure display names for axis titles
  const dimensionCol = columns.find(c => c.name === dimensionColumnName);
  const xAxisTitle = dimensionCol?.displayName || tableData.headers[dimensionIndex];

  const result = {
    type: typeMap[chartType] || 'bar',
    categories,
    series,
    xAxisTitle,
    showLegend: series.length > 1
  };

  return result;
};

export default transformTableToChartData;
